/**
 * Stack Selection Step Component
 *
 * Allows clients to select their technology stack during onboarding.
 * This selection will inform document generation and compliance requirements.
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React from 'react';
import { Card, Label, Badge, Alert } from 'flowbite-react';
import {
  HiCode,
  HiServer,
  HiCloud,
  HiDatabase,
  HiCube,
  HiInformationCircle
} from 'react-icons/hi';

export interface StackSelection {
  frontendStack: string[];
  backendStack: string[];
  cloudProvider: string[];
  database: string[];
  architecture: string[];
  additionalTechnologies?: string;
}

interface StackSelectionStepProps {
  stackSelection: StackSelection;
  onChange: (stackSelection: StackSelection) => void;
}

interface StackOption {
  id: string;
  name: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

const FRONTEND_OPTIONS: StackOption[] = [
  { id: 'react', name: 'React', description: 'React with TypeScript' },
  { id: 'react-nextjs', name: 'Next.js', description: 'React framework' },
  { id: 'vue', name: 'Vue.js', description: 'Progressive framework' },
  { id: 'angular', name: 'Angular', description: 'Full framework' },
  { id: 'svelte', name: 'Svelte', description: 'Compiler-based' },
  { id: 'vanilla', name: 'Vanilla JS', description: 'No framework' },
  { id: 'other-frontend', name: 'Other', description: 'Custom frontend' }
];

const BACKEND_OPTIONS: StackOption[] = [
  { id: 'node-express', name: 'Node.js/Express', description: 'JavaScript runtime' },
  { id: 'node-nestjs', name: 'NestJS', description: 'TypeScript framework' },
  { id: 'node-serverless', name: 'Serverless/Lambda', description: 'AWS Lambda Node.js' },
  { id: 'python-django', name: 'Django', description: 'Python web framework' },
  { id: 'python-flask', name: 'Flask', description: 'Python micro-framework' },
  { id: 'python-fastapi', name: 'FastAPI', description: 'Modern Python API' },
  { id: 'java-spring', name: 'Spring Boot', description: 'Java framework' },
  { id: 'go', name: 'Go', description: 'Go language' },
  { id: 'ruby-rails', name: 'Ruby on Rails', description: 'Ruby framework' },
  { id: 'dotnet', name: '.NET/C#', description: 'Microsoft stack' },
  { id: 'other-backend', name: 'Other', description: 'Custom backend' }
];

const CLOUD_OPTIONS: StackOption[] = [
  { id: 'aws', name: 'AWS', description: 'Amazon Web Services' },
  { id: 'azure', name: 'Azure', description: 'Microsoft Azure' },
  { id: 'gcp', name: 'Google Cloud', description: 'Google Cloud Platform' },
  { id: 'multi-cloud', name: 'Multi-cloud', description: 'Multiple providers' },
  { id: 'on-premises', name: 'On-premises', description: 'Self-hosted' },
  { id: 'hybrid', name: 'Hybrid', description: 'Cloud and on-premises' }
];

const DATABASE_OPTIONS: StackOption[] = [
  { id: 'postgresql', name: 'PostgreSQL', description: 'Relational database' },
  { id: 'mysql', name: 'MySQL/MariaDB', description: 'Relational database' },
  { id: 'mongodb', name: 'MongoDB', description: 'Document database' },
  { id: 'dynamodb', name: 'DynamoDB', description: 'AWS NoSQL' },
  { id: 'redis', name: 'Redis', description: 'In-memory data store' },
  { id: 'elasticsearch', name: 'Elasticsearch', description: 'Search engine' },
  { id: 'other-database', name: 'Other', description: 'Custom database' }
];

const ARCHITECTURE_OPTIONS: StackOption[] = [
  { id: 'serverless', name: 'Serverless', description: 'AWS Lambda, Azure Functions' },
  { id: 'microservices', name: 'Microservices', description: 'Distributed services' },
  { id: 'monolith', name: 'Monolith', description: 'Single application' },
  { id: 'event-driven', name: 'Event-driven', description: 'Message-based' },
  { id: 'hybrid-arch', name: 'Hybrid', description: 'Mixed architecture' }
];

export const StackSelectionStep: React.FC<StackSelectionStepProps> = ({
  stackSelection,
  onChange
}) => {
  const toggleSelection = (category: keyof StackSelection, value: string) => {
    const currentSelection = stackSelection[category] as string[];
    const newSelection = currentSelection.includes(value)
      ? currentSelection.filter(item => item !== value)
      : [...currentSelection, value];

    onChange({
      ...stackSelection,
      [category]: newSelection
    });
  };

  const handleAdditionalTechChange = (value: string) => {
    onChange({
      ...stackSelection,
      additionalTechnologies: value
    });
  };

  const renderSelectionGrid = (
    category: keyof StackSelection,
    options: StackOption[],
    icon: React.ComponentType<{ className?: string }>,
    title: string,
    description: string
  ) => {
    const Icon = icon;
    const selections = stackSelection[category] as string[];

    return (
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Icon className="w-5 h-5 text-indigo-600" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-600">{description}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {options.map((option) => {
            const isSelected = selections.includes(option.id);
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => toggleSelection(category, option.id)}
                className={`p-3 border-2 rounded-lg text-left transition-all ${
                  isSelected
                    ? 'border-indigo-600 bg-indigo-50'
                    : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-gray-900 text-sm">
                  {option.name}
                </div>
                {option.description && (
                  <div className="text-xs text-gray-500 mt-1">
                    {option.description}
                  </div>
                )}
                {isSelected && (
                  <Badge color="indigo" size="xs" className="mt-2">
                    Selected
                  </Badge>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const hasSelections =
    stackSelection.frontendStack.length > 0 ||
    stackSelection.backendStack.length > 0 ||
    stackSelection.cloudProvider.length > 0 ||
    stackSelection.database.length > 0 ||
    stackSelection.architecture.length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Technology Stack</h2>
        <p className="text-gray-600 mt-1">
          Select the technologies your organization uses. This will help us provide relevant
          standards and documentation.
        </p>
      </div>

      {!hasSelections && (
        <Alert color="info" icon={HiInformationCircle}>
          <span className="font-medium">Select at least one technology</span> from any category
          to help us tailor the platform to your needs.
        </Alert>
      )}

      <div className="space-y-6">
        {renderSelectionGrid(
          'frontendStack',
          FRONTEND_OPTIONS,
          HiCode,
          'Frontend Stack',
          'Select your frontend frameworks and technologies'
        )}

        {renderSelectionGrid(
          'backendStack',
          BACKEND_OPTIONS,
          HiServer,
          'Backend Stack',
          'Select your backend frameworks and technologies'
        )}

        {renderSelectionGrid(
          'cloudProvider',
          CLOUD_OPTIONS,
          HiCloud,
          'Cloud Provider',
          'Select your cloud infrastructure provider(s)'
        )}

        {renderSelectionGrid(
          'database',
          DATABASE_OPTIONS,
          HiDatabase,
          'Database Technology',
          'Select your database systems'
        )}

        {renderSelectionGrid(
          'architecture',
          ARCHITECTURE_OPTIONS,
          HiCube,
          'Architecture Pattern',
          'Select your architectural approach'
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="additionalTech">
          Additional Technologies (Optional)
        </Label>
        <textarea
          id="additionalTech"
          value={stackSelection.additionalTechnologies || ''}
          onChange={(e) => handleAdditionalTechChange(e.target.value)}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
          placeholder="List any other technologies, tools, or frameworks you use (e.g., Docker, Kubernetes, GraphQL, etc.)"
        />
      </div>

      {hasSelections && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <HiInformationCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-indigo-800">
              <p className="font-medium mb-1">Your selections will help us:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Recommend relevant standards and best practices</li>
                <li>Generate technology-specific documentation templates</li>
                <li>Provide targeted compliance guidance</li>
                <li>Suggest appropriate workflow configurations</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StackSelectionStep;
